import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useGetCurrentUserQuery } from '../api/authApi';
import { AuthUserResponse } from '../api/authApi';
import { useGetUserPermissionsQuery } from '../api/rbacApi';
import { RoleName } from '../types/auth';
import { getRoleName } from '../utils/roleUtils';

interface RbacContextType {
  hasPermission: (permission: string) => boolean;
  hasRole: (role: RoleName) => boolean;
  canManage: (permission: string) => boolean;
  permissions: string[];
  roles: string[];
  user: AuthUserResponse | undefined;
  loading: boolean;
}

const RbacContext = createContext<RbacContextType | undefined>(undefined);

function checkPermissionAccess(
  userPermissions: string[],
  permission: string,
  userRole?: string,
): boolean {
  if (userRole === 'admin') {
    return true;
  }

  if (userPermissions.includes('admin:all')) {
    return true;
  }

  const prefix = permission.split(':')[0];
  const wildcardPermission = `${prefix}:all`;
  if (userPermissions.includes(wildcardPermission)) {
    return true;
  }

  return userPermissions.includes(permission);
}

export const RbacProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = localStorage.getItem('token');
  const { data: user } = useGetCurrentUserQuery(undefined, {
    skip: !token,
  });
  const { data: userPermissionsResponse } = useGetUserPermissionsQuery(undefined, {
    skip: !token,
  });
  const [permissions, setPermissions] = useState<string[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser && parsedUser.role) {
          const roleName = getRoleName(parsedUser.role);
          setRoles([roleName]);
        }
      } catch (error) {
        console.error('Error parsing stored user:', error);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (user) {
      const roleName = getRoleName(user.role);
      setRoles([roleName]);
    }
  }, [user]);

  useEffect(() => {
    if (userPermissionsResponse?.permissions) {
      setPermissions(userPermissionsResponse.permissions);
    } else {
      const currentRole = roles[0];
      if (currentRole) {
        setPermissions(getDefaultPermissions(currentRole));
      }
    }
  }, [userPermissionsResponse, roles]);

  const hasPermission = useCallback(
    (permission: string): boolean => {
      const currentRole = roles[0];
      return checkPermissionAccess(permissions, permission, currentRole);
    },
    [permissions, roles],
  );

  const hasRole = useCallback(
    (role: RoleName): boolean => {
      return roles.includes(role);
    },
    [roles],
  );

  const canManage = useCallback(
    (permission: string): boolean => {
      return hasPermission(permission);
    },
    [hasPermission],
  );

  return (
    <RbacContext.Provider
      value={{
        hasPermission,
        hasRole,
        canManage,
        permissions,
        roles,
        user,
        loading,
      }}
    >
      {children}
    </RbacContext.Provider>
  );
};

function getDefaultPermissions(role: string): string[] {
  const defaultPermissions: Record<string, string[]> = {
    admin: [
      'admin:all',
      'admin:dashboard',
      'admin:users',
      'admin:roles',
      'admin:settings',
      'admin:content',
      'admin:theme',
      'admin:email',
      'admin:notifications',
      'admin:business',
      'admin:production',
      'admin:inventory',
      'admin:orders',
      'admin:customers',
      'admin:products',
      'admin:quality',
      'admin:maintenance',
      'admin:supply-chain',
      'admin:payments',
      'admin:analytics',
      'admin:reports',
      'admin:performance',
      'admin:system',
    ],
    manager: [
      'manager:all',
      'manager:dashboard',
      'manager:production',
      'manager:inventory',
      'manager:orders',
      'manager:customers',
      'manager:quality',
      'manager:maintenance',
      'manager:products',
      'manager:supply-chain',
      'manager:reports',
    ],
    worker: [
      'worker:all',
      'worker:dashboard',
      'worker:production',
      'worker:maintenance',
      'worker:quality',
      'worker:products',
      'worker:reports',
      'worker:tasks',
    ],
    customer: [
      'customer:all',
      'customer:dashboard',
      'customer:orders',
      'customer:profile',
      'customer:notifications',
    ],
  };
  return defaultPermissions[role] || [];
}

export const useRbac = () => {
  const context = useContext(RbacContext);
  if (context === undefined) {
    throw new Error('useRbac must be used within an RbacProvider');
  }
  return {
    ...context,
    user: context.user,
    hasPermission: context.hasPermission,
    hasRole: context.hasRole,
    canManage: context.canManage,
    permissions: context.permissions,
    roles: context.roles,
    loading: context.loading,
  };
};
